import { useState } from 'react'
import { useR2List } from '../hooks/useR2Browser'
import type { R2ObjectInfo } from '../types'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Card } from '@/components/ui/card'
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/ui/breadcrumb'
import { Folder, File, ChevronRight } from 'lucide-react'

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

const formatDate = (dateStr: string) => {
  const d = new Date(dateStr)
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return `${String(d.getDate()).padStart(2, '0')}-${months[d.getMonth()]}-${d.getFullYear()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

function PrefixBreadcrumb({ prefix, onNavigate }: { prefix: string; onNavigate: (p: string) => void }) {
  const parts = prefix.split('/').filter(Boolean)

  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          {parts.length > 0 ? (
            <BreadcrumbLink className="cursor-pointer" onClick={() => onNavigate('')}>
              Root
            </BreadcrumbLink>
          ) : (
            <BreadcrumbPage>Root</BreadcrumbPage>
          )}
        </BreadcrumbItem>
        {parts.map((part, i) => {
          const subPrefix = parts.slice(0, i + 1).join('/') + '/'
          const isLast = i === parts.length - 1
          return (
            <BreadcrumbItem key={subPrefix}>
              <BreadcrumbSeparator />
              {isLast ? (
                <BreadcrumbPage>{part}</BreadcrumbPage>
              ) : (
                <BreadcrumbLink className="cursor-pointer" onClick={() => onNavigate(subPrefix)}>
                  {part}
                </BreadcrumbLink>
              )}
            </BreadcrumbItem>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}

function ObjectDetailDialog({ object, open, onOpenChange }: {
  object: R2ObjectInfo | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  if (!object) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm break-all">{object.key}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
            <span className="text-muted-foreground">Size</span>
            <span>{formatBytes(object.size)} ({object.size.toLocaleString()} bytes)</span>
            <span className="text-muted-foreground">ETag</span>
            <span className="font-mono text-xs">{object.etag}</span>
            <span className="text-muted-foreground">Last Modified</span>
            <span>{formatDate(object.lastModified)}</span>
            {object.httpMetadata?.contentType && (
              <>
                <span className="text-muted-foreground">Content Type</span>
                <span>{object.httpMetadata.contentType}</span>
              </>
            )}
            {object.httpMetadata?.contentEncoding && (
              <>
                <span className="text-muted-foreground">Encoding</span>
                <span>{object.httpMetadata.contentEncoding}</span>
              </>
            )}
          </div>
          {object.customMetadata && Object.keys(object.customMetadata).length > 0 && (
            <div>
              <h4 className="font-medium mb-1">Custom Metadata</h4>
              <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-xs">
                {Object.entries(object.customMetadata).map(([k, v]) => (
                  <><span key={`k-${k}`} className="text-muted-foreground">{k}</span><span key={`v-${k}`}>{v}</span></>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function R2BucketBrowser({ bucket }: { bucket: string }) {
  const [prefix, setPrefix] = useState('')
  const [selectedObject, setSelectedObject] = useState<R2ObjectInfo | null>(null)
  const { result, loading, error } = useR2List(bucket, prefix)

  const folders = (result?.delimitedPrefixes || []).map(p => {
    // Strip the current prefix to get just the folder name
    const name = p.slice(prefix.length).replace(/\/$/, '')
    return { prefix: p, name }
  })

  const files = result?.objects || []

  return (
    <div>
      <div className="mb-4">
        <PrefixBreadcrumb prefix={prefix} onNavigate={setPrefix} />
      </div>

      <ObjectDetailDialog
        object={selectedObject}
        open={!!selectedObject}
        onOpenChange={(open) => { if (!open) setSelectedObject(null) }}
      />

      {error && (
        <div className="text-sm text-destructive mb-4">Failed to list objects: {error}</div>
      )}

      {loading ? (
        <div className="space-y-2">
          {[1, 2, 3].map(i => <Skeleton key={i} className="h-10 w-full" />)}
        </div>
      ) : folders.length === 0 && files.length === 0 ? (
        <div className="text-sm text-muted-foreground py-8 text-center">
          {prefix ? 'This directory is empty.' : 'This bucket is empty.'}
        </div>
      ) : (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead className="w-28 text-right">Size</TableHead>
                <TableHead className="w-44">Last Modified</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {folders.map(f => (
                <TableRow
                  key={f.prefix}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => setPrefix(f.prefix)}
                >
                  <TableCell className="w-8 px-3">
                    <Folder className="size-4 text-amber-500" />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-1">
                      {f.name}
                      <ChevronRight className="size-3.5 text-muted-foreground" />
                    </div>
                  </TableCell>
                  <TableCell className="text-right text-muted-foreground">—</TableCell>
                  <TableCell className="text-muted-foreground">—</TableCell>
                </TableRow>
              ))}
              {files.map(obj => {
                const fileName = obj.key.slice(prefix.length)
                return (
                  <TableRow
                    key={obj.key}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => setSelectedObject(obj)}
                  >
                    <TableCell className="w-8 px-3">
                      <File className="size-4 text-muted-foreground" />
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm">{fileName}</span>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground whitespace-nowrap">
                      {formatBytes(obj.size)}
                    </TableCell>
                    <TableCell className="text-muted-foreground whitespace-nowrap">
                      {formatDate(obj.lastModified)}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  )
}

export function R2BrowserPage() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">R2 Browser</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Browse object storage buckets (read-only).
        </p>
      </div>

      <Tabs defaultValue="seeds">
        <TabsList>
          <TabsTrigger value="seeds">Seeds Storage</TabsTrigger>
          <TabsTrigger value="geo">Geo Tiles</TabsTrigger>
        </TabsList>
        <TabsContent value="seeds" className="mt-6">
          <R2BucketBrowser bucket="seeds" />
        </TabsContent>
        <TabsContent value="geo" className="mt-6">
          <R2BucketBrowser bucket="geo" />
        </TabsContent>
      </Tabs>
    </div>
  )
}
